/**
 * columnSizing — util PURO de dimensionamento (largura) de colunas da List
 * view de tarefas. Modelo PX-CONTÍNUO (v3): a largura é persistida em PIXELS
 * inteiros e o arraste é 1:1 (cada pixel de gesto move 1px da coluna), SEM
 * snap a presets. Cada coluna tem bounds próprios (`minPx`/`maxPx`, FE-only)
 * que ficam DENTRO do range global `[MIN_PX_GLOBAL, MAX_PX_GLOBAL]` para
 * sempre passar na validação do backend.
 *
 * Contrato FE↔BE (congelado):
 *   - width persistido `<= 12` é valor LEGADO em cols (v2) → convertido só na
 *     LEITURA via `legacyColsToPx` (`px = round((cols/12)*REFERENCE_CONTAINER_PX)`
 *     depois clampado nos bounds da coluna). Conversão NÃO re-persiste — só
 *     normaliza pra px no próximo save daquela coluna (lazy).
 *   - width persistido `>= 40` é px (clampado nos bounds da coluna + global).
 *   - gap 13–39 é inalcançável; se aparecer, tratado como px e clampado.
 *
 * Sem optional chaining / nullish coalescing (Babel `@vue/app` não inclui o
 * plugin) — usar `v && v.x`.
 */

// Range global de validação (espelha os limites do backend). Toda largura
// final cai obrigatoriamente neste intervalo.
export const MIN_PX_GLOBAL = 40
export const MAX_PX_GLOBAL = 1200

// Container de referência usado para converter o valor legado em cols (base
// 12 do grid Vuetify) para px na leitura retrocompatível.
export const REFERENCE_CONTAINER_PX = 1200

// Largura default (px) quando o descriptor usa `cols: 'auto'` (sem cols
// numérico de partida). Serve de `startPx` ao arrastar uma coluna `auto` nunca
// redimensionada.
export const DEFAULT_PX_BY_FALLBACK = 120

// Converte um valor para número finito; usa `fallback` quando não-numérico.
function _toFinite(value, fallback) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

/**
 * Clampa `px` no range da coluna (`[minPx, maxPx]`) e depois no range global
 * (`[MIN_PX_GLOBAL, MAX_PX_GLOBAL]`). Bounds da coluna ausentes/inválidos caem
 * no range global. Bounds invertidos são tolerados defensivamente.
 *
 * @param {number} px - largura bruta em pixels.
 * @param {number} [minPx] - menor largura permitida para a coluna.
 * @param {number} [maxPx] - maior largura permitida para a coluna.
 * @returns {number} px clampado (inteiro, já que o caller arredonda).
 */
export function clampPx(px, minPx, maxPx) {
  const rawMin = _toFinite(minPx, MIN_PX_GLOBAL)
  const rawMax = _toFinite(maxPx, MAX_PX_GLOBAL)
  // Tolera bounds invertidos (min > max).
  let colMin = Math.min(rawMin, rawMax)
  let colMax = Math.max(rawMin, rawMax)
  // Mantém os bounds da coluna dentro do range global.
  colMin = Math.max(colMin, MIN_PX_GLOBAL)
  colMax = Math.min(colMax, MAX_PX_GLOBAL)
  if (colMin > colMax) colMin = colMax
  const value = _toFinite(px, colMin)
  if (value < colMin) return colMin
  if (value > colMax) return colMax
  return value
}

/**
 * Resolve a nova largura (px) durante o arraste: tracking 1:1 SEM snap.
 * `clampPx(round(startPx + deltaPx), minPx, maxPx)`.
 *
 * @param {object} params
 * @param {number} params.startPx - largura no início do arraste (px).
 * @param {number} params.deltaPx - deslocamento horizontal (clientX - startX).
 * @param {number} [params.minPx] - menor largura permitida para a coluna.
 * @param {number} [params.maxPx] - maior largura permitida para a coluna.
 * @returns {number} nova largura em px, clampada.
 */
export function resizePx(params) {
  const p = params || {}
  const startPx = _toFinite(p.startPx, DEFAULT_PX_BY_FALLBACK)
  const deltaPx = _toFinite(p.deltaPx, 0)
  return clampPx(Math.round(startPx + deltaPx), p.minPx, p.maxPx)
}

/**
 * Converte um valor LEGADO em cols (grid base 12) para px, clampado nos bounds
 * da coluna: `clampPx(round((cols/12)*REFERENCE_CONTAINER_PX), minPx, maxPx)`.
 * Usado só na LEITURA retrocompatível de preferências v2.
 *
 * @param {number} cols - largura legada em cols (1..12).
 * @param {number} [minPx] - menor largura permitida para a coluna.
 * @param {number} [maxPx] - maior largura permitida para a coluna.
 * @returns {number} px equivalente, clampado.
 */
export function legacyColsToPx(cols, minPx, maxPx) {
  const c = _toFinite(cols, 0)
  const px = Math.round((c / 12) * REFERENCE_CONTAINER_PX)
  return clampPx(px, minPx, maxPx)
}

/**
 * Resolve o `startPx` default de um descriptor de coluna — a largura de
 * partida quando uma coluna nunca redimensionada é arrastada. Precedência:
 *   1. `defaultPx` explícito no descriptor (px já na unidade final). É o que
 *      aperta a régua: sem ele, uma coluna de pessoa nasceria com os 200px do
 *      `cols: '2'` e uma coluna compacta com os 120px do fallback — larguras
 *      pensadas para conteúdo que hoje é bem mais estreito (avatar, bolinha
 *      de status, bandeirinha de prioridade).
 *   2. `cols` numérico (ex.: title `'3'`) → col→px via `legacyColsToPx`.
 *   3. `cols: 'auto'` sem `defaultPx` → `DEFAULT_PX_BY_FALLBACK`.
 * Todos clampados nos bounds da coluna — exceto o `defaultPx` de coluna
 * NÃO-redimensionável, que vale literalmente (ver comentário no corpo).
 *
 * @param {object} descriptor - descriptor do getter `visibleListColumns`.
 * @returns {number} px default de partida.
 */
export function defaultPxFor(descriptor) {
  const d = descriptor || {}
  const explicitPx = Number(d.defaultPx)
  if (Number.isFinite(explicitPx) && explicitPx > 0) {
    // Coluna NÃO-redimensionável (hoje só `actions`) nunca tem largura
    // persistida — não passa pela validação [40,1200] do backend — então o
    // `defaultPx` vale como largura final mesmo abaixo do piso global. É o que
    // permite a faixa vazia antes do `+` medir 16px em vez dos 40px do piso.
    if (d.resizable === false) return Math.round(explicitPx)
    return clampPx(Math.round(explicitPx), d.minPx, d.maxPx)
  }
  const cols = Number(d.cols)
  if (Number.isFinite(cols) && cols > 0) {
    return legacyColsToPx(cols, d.minPx, d.maxPx)
  }
  return clampPx(DEFAULT_PX_BY_FALLBACK, d.minPx, d.maxPx)
}

/**
 * Monta o objeto `flexStyle` (binding `:style`) para uma célula da List.
 *
 * Modelo SHRINK-TO-FIT (v4 — estilo ClickUp): a régua SEMPRE cabe em 100% da
 * largura disponível, SEM scroll horizontal. A largura px é a largura
 * PREFERIDA da coluna (respeitada quando há espaço de sobra); quando faltam
 * pixels, as colunas encolhem proporcionalmente (`flex-shrink: 1` +
 * `min-width: 0`, truncando o conteúdo) até tudo caber.
 *
 *   - rígido (largura preferida em px): `flex: '0 1 <px>px'` — NÃO cresce além
 *     de `<px>` (a folga vai pro grower via `maxWidth`), mas ENCOLHE quando
 *     necessário. `minWidth: '0px'` libera o item a comprimir abaixo do
 *     conteúdo (sobrescreve o `min-width: auto` default do flexbox).
 *   - grower (`isGrower: true`, hoje só a coluna `title` sem px): ocupa o
 *     espaço livre e é o primeiro a ceder até seu piso legível:
 *     `flex: '1 1 <minPx>px'`, `minWidth: '<minPx>px'`. O piso do grower é o
 *     único `min-width` > 0 da linha — garante que o nome continua legível
 *     enquanto as demais colunas absorvem a compressão.
 *
 *   - rígido COM PISO (`shrinkFloor: true`, hoje só `priority`): encolhe como
 *     qualquer coluna rígida, mas NÃO abaixo do próprio `minPx`
 *     (`minWidth: '<minPx>px'`). Serve para conteúdo compacto que fica
 *     ilegível — e não só truncado — quando comprimido até quase zero.
 *
 *   - rígido NÃO-COMPRIMÍVEL (`noShrink: true`, hoje só `status`): mantém a
 *     largura px como PISO real (`flex: '0 0 <px>px'` + `minWidth: <px>px`).
 *     Sem isso o chip de status, comprimido pelo shrink-to-fit, encosta/invade
 *     visualmente a coluna de prioridade. O piso efetivo é o `minPx` da coluna
 *     (o clamp da leitura e do arraste já garante `px >= minPx`).
 *
 * @param {object} params
 * @param {number} [params.px] - largura PREFERIDA em px (modo rígido).
 * @param {boolean} [params.isGrower] - true para a coluna que ocupa o resto.
 * @param {number} [params.minPx] - largura mínima do grower (flex-basis).
 * @param {boolean} [params.noShrink] - true para a coluna rígida que NÃO pode
 *   ser comprimida abaixo da própria largura px.
 * @param {boolean} [params.shrinkFloor] - true para a coluna rígida que encolhe
 *   apenas até `minPx` (piso real em vez de `minWidth: 0`).
 * @returns {object} objeto de estilo pronto para `:style` (sem chamada no
 *   template — o getter já entrega `flexStyle` montado).
 */
export function flexStyleFor(params) {
  const p = params || {}
  if (p.isGrower) {
    const basis = _toFinite(p.minPx, MIN_PX_GLOBAL)
    return {
      flex: '1 1 ' + basis + 'px',
      minWidth: basis + 'px',
    }
  }
  const px = _toFinite(p.px, DEFAULT_PX_BY_FALLBACK)
  if (p.noShrink) {
    return {
      flex: '0 0 ' + px + 'px',
      maxWidth: px + 'px',
      minWidth: px + 'px',
    }
  }
  // Piso de compressão: nunca abaixo de `minPx` — mas também nunca acima da
  // largura preferida (uma coluna com px < minPx não deve CRESCER pelo piso).
  const floorPx = p.shrinkFloor ? Math.min(_toFinite(p.minPx, 0), px) : 0
  return {
    flex: '0 1 ' + px + 'px',
    maxWidth: px + 'px',
    minWidth: floorPx + 'px',
  }
}
